export const parseMaybeJson = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const hasValue = (value) => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.some(hasValue);
  if (typeof value === 'object') return Object.values(value).some(hasValue);
  return true;
};

export const hasOnfleetCompletionSignal = (details) => {
  if (!details || typeof details !== 'object') return false;

  if (
    Object.prototype.hasOwnProperty.call(details, 'success') &&
    details.success !== null &&
    details.success !== undefined
  ) {
    return true;
  }

  return [
    'completionTime',
    'completion_time',
    'completedAt',
    'completed_at',
    'time',
    'photoUploadId',
    'photoUploadIds',
    'photoUploadID',
    'photoUploadIDs',
    'photoUploads',
    'photos',
    'attachments',
    'signatureUploadId',
    'localPhoto',
    'localPhotos',
    'localSignature',
    'notes',
    'failureNotes',
    'successNotes',
    'events'
  ].some((key) => hasValue(details[key]));
};

const normalizeCompletionPayload = (details) => {
  if (!hasOnfleetCompletionSignal(details)) return null;
  const notes = details.notes || details.completionNotes || details.completion_notes
    || details.failureNotes || details.failure_notes || details.successNotes || details.success_notes;
  return notes ? { ...details, notes } : details;
};

export const normalizeCompletionDetails = (source) => {
  const parsed = parseMaybeJson(source);
  if (!parsed) return null;

  if (parsed.completionDetails) return normalizeCompletionPayload(parsed.completionDetails);
  if (parsed.task?.completionDetails) return normalizeCompletionPayload(parsed.task.completionDetails);
  if (parsed.data?.task?.completionDetails) return normalizeCompletionPayload(parsed.data.task.completionDetails);

  if (
    Object.prototype.hasOwnProperty.call(parsed, 'success') ||
    parsed.completionTime ||
    parsed.photoUploadId ||
    parsed.photoUploadIds ||
    parsed.photoUploadID ||
    parsed.photoUploadIDs ||
    parsed.signatureUploadId ||
    parsed.localPhoto ||
    parsed.localPhotos ||
    parsed.localSignature
  ) {
    return normalizeCompletionPayload(parsed);
  }

  return null;
};

export const getOrderCompletionDetails = (order, taskDetails = null) => {
  return (
    normalizeCompletionDetails(taskDetails) ||
    normalizeCompletionDetails(order?.completion_details) ||
    normalizeCompletionDetails(order?.onfleet_task_json) ||
    normalizeCompletionDetails(order?.task_info)
  );
};

export const getOrderDriverNote = (order, taskDetails = null) => {
  const completionDetails = getOrderCompletionDetails(order, taskDetails);

  return completionDetails?.notes
    || order?.failure_notes
    || order?.failureNotes
    || order?.driver_note
    || order?.driverNote
    || null;
};

export const getOrderCompletionTime = (order, taskDetails = null) => {
  const details = getOrderCompletionDetails(order, taskDetails);
  const isFinalOrderStatus = ['Completed', 'Collected', 'Failed'].includes(order?.order_status);

  return (
    details?.completionTime ||
    details?.completion_time ||
    details?.completedAt ||
    details?.completed_at ||
    details?.time ||
    (isFinalOrderStatus ? order?.onfleet_status_time : null) ||
    null
  );
};

const toArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const collectUploadRefs = (value) => {
  return toArray(value).flatMap((item) => {
    if (!item) return [];
    if (typeof item === 'string' || typeof item === 'number') return [String(item)];
    if (Array.isArray(item)) return collectUploadRefs(item);
    if (typeof item !== 'object') return [];

    const keys = [
      'id',
      'uploadId',
      'uploadID',
      'photoUploadId',
      'photoUploadID',
      'photoUploadIds',
      'photoUploadIDs',
      'url',
      'src'
    ];

    return [
      ...keys.flatMap((key) => collectUploadRefs(item[key]))
    ];
  });
};

const normalizeImageUrl = (ref, size = '800x') => {
  if (!ref) return null;
  if (/^https?:\/\//i.test(ref) || /^data:image\//i.test(ref)) return ref;
  return `https://d15p8tr8p0vffz.cloudfront.net/${ref}/${size}.png`;
};

export const getOnfleetImageUrls = (details) => {
  if (!details) return [];

  const images = [];
  const seen = new Set();
  const addImage = (ref, label, size = '800x') => {
    const url = normalizeImageUrl(ref, size);
    if (!url || seen.has(url)) return;
    seen.add(url);
    images.push({ url, label });
  };

  const localPhotoIds = new Set();
  const localPhotoUrls = toArray(details.localPhotos).flatMap((item) => {
    if (!item) return [];
    if (typeof item === 'string') return [item];
    if (typeof item !== 'object') return [];
    if (item.id || item.uploadId || item.uploadID) {
      localPhotoIds.add(String(item.id || item.uploadId || item.uploadID));
    }
    return [item.url, item.src, item.localUrl].filter(Boolean);
  });

  localPhotoUrls.forEach((ref, index) => {
    addImage(ref, `Delivery photo ${localPhotoUrls.length > 1 ? index + 1 : ''}`.trim());
  });

  if (details.localPhoto) {
    addImage(details.localPhoto, 'Delivery photo');
  }

  const photoRefs = [
    ...collectUploadRefs(details.photoUploadId),
    ...collectUploadRefs(details.photoUploadID),
    ...collectUploadRefs(details.photoUploadIds),
    ...collectUploadRefs(details.photoUploadIDs),
    ...collectUploadRefs(details.photoUploads),
    ...collectUploadRefs(details.photos),
    ...collectUploadRefs(details.attachments),
    ...collectUploadRefs(details.photoUrls),
    ...collectUploadRefs(details.photoURL)
  ];

  const remotePhotoRefs = photoRefs.filter((ref) => !localPhotoIds.has(String(ref)));

  remotePhotoRefs.forEach((ref, index) => {
    addImage(ref, `Delivery photo ${remotePhotoRefs.length > 1 ? index + 1 : ''}`.trim());
  });

  if (details.signatureUploadId) {
    addImage(details.signatureUploadId, 'Signature', '282x');
  }

  if (details.localSignature) {
    addImage(details.localSignature, 'Signature');
  }

  return images;
};

export const getTrackingInfo = (order) => {
  const parsed = parseMaybeJson(order?.task_info) || parseMaybeJson(order?.onfleet_task_json) || {};
  const shortId = parsed.shortId || parsed.task?.shortId || parsed.data?.task?.shortId;
  const trackingURL = parsed.trackingURL || parsed.trackingUrl || parsed.task?.trackingURL || parsed.data?.task?.trackingURL;

  return {
    shortId,
    trackingURL,
    finalTrackingUrl: trackingURL || (shortId ? `https://onfleet.com/t/${shortId}` : null)
  };
};
